import React, { useEffect } from 'react'
import { getUserInfoSync } from '../../config/api'

const SideBar = ({ activeScreen, setActiveScreen, isFirstLogin }) => {
  const userInfo = getUserInfoSync()
  const firstLogin = isFirstLogin || userInfo?.first_login === true

  const handleMenuClick = (screen) => {
    // If first login, only allow profile screen
    if (firstLogin && screen !== 'profile') {
      return
    }
    setActiveScreen(screen)
  }

  const isActive = (screenOrScreens) => {
    if (Array.isArray(screenOrScreens)) {
      return screenOrScreens.includes(activeScreen) ? '' : 'collapsed'
    }
    return activeScreen === screenOrScreens ? '' : 'collapsed'
  }

  const isSubActive = (screen) => {
    return activeScreen === screen ? 'active' : ''
  }

  const isExpanded = (screens) => {
    return screens.includes(activeScreen) ? 'collapse show' : 'collapse'
  }

  // Ensure parent menus are expanded when a sub-item is active
  useEffect(() => {
    const menuMappings = {
      'create-users': '#users-nav',
      'manage-users': '#users-nav',
      'access-logs': '#users-nav',
      'add-products': '#products-nav',
      'manage-products': '#products-nav',
      'print-barcodes': '#products-nav',
      'import-from-excel': '#products-nav',
      'new-stock-entry': '#stocks-nav',
      'manage-stocks': '#stocks-nav',
      'stock-adjustments': '#stocks-nav',
      'stock-movement': '#stocks-nav',
      'return-to-warehouse': '#stocks-nav',
      'transfer-stocks': '#stocks-nav',
      'low-stock': '#stocks-nav',
      'add-categories': '#categories-nav',
      'manage-categories': '#categories-nav',
      'add-branch': '#branches-nav',
      'manage-branches': '#branches-nav',
      'add-warehouse': '#warehouses-nav',
      'manage-warehouses': '#warehouses-nav',
      'add-expense': '#expenses-nav',
      'manage-expenses': '#expenses-nav',
      'create-expense-category': '#expenses-nav',
      'manage-expense-category': '#expenses-nav',
      'add-supplier': '#suppliers-nav',
      'manage-suppliers': '#suppliers-nav',
      'sales': '#sales-nav',
      'set-discounts': '#sales-nav',
      'manage-discounts': '#sales-nav',
      'exchange-rate': '#sales-nav',
      'admin-layby': '#sales-nav',
      'reports': '#reports-nav',
      'profitnloss': '#reports-nav'
    }

    const targetId = menuMappings[activeScreen]
    if (targetId) {
      const collapseElement = document.querySelector(targetId)
      if (collapseElement && window.bootstrap && window.bootstrap.Collapse) {
        let bsCollapse = window.bootstrap.Collapse.getInstance(collapseElement)
        if (!bsCollapse) {
          bsCollapse = new window.bootstrap.Collapse(collapseElement, { toggle: false })
        }
        if (!collapseElement.classList.contains('show')) {
          bsCollapse.show()
        }
      }
    }
  }, [activeScreen])

  return (
    <aside id="sidebar" className="sidebar">
      <ul className="sidebar-nav" id="sidebar-nav">
        {/* Dashboard */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive('dashboard')} ${firstLogin ? 'disabled' : ''}`}
            href="#"
            onClick={(e) => { e.preventDefault(); handleMenuClick('dashboard'); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-grid" />
            <span>Dashboard</span>
          </a>
        </li>{/* End Dashboard Nav */}

        {/* Users */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive(['create-users', 'manage-users', 'access-logs'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#users-nav"
            data-bs-toggle={firstLogin ? '' : 'collapse'}
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-people" />
            <span>Users</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="users-nav" className={`nav-content ${isExpanded(['create-users', 'manage-users', 'access-logs'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('create-users'); }}
                className={isSubActive('create-users')}
              >
                <i className="bi bi-circle" />
                <span>Create Users</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-users'); }}
                className={isSubActive('manage-users')}
              >
                <i className="bi bi-circle" />
                <span>Manage Users</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('access-logs'); }}
                className={isSubActive('access-logs')}
              >
                <i className="bi bi-circle" />
                <span>Access Logs</span>
              </a>
            </li>
          </ul>
        </li>{/* End Users Nav */}

        {/* Products */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive(['add-products', 'manage-products', 'print-barcodes', 'import-from-excel'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#products-nav"
            data-bs-toggle={firstLogin ? '' : 'collapse'}
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-box-seam" />
            <span>Products</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="products-nav" className={`nav-content ${isExpanded(['add-products', 'manage-products', 'print-barcodes', 'import-from-excel'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('add-products'); }}
                className={isSubActive('add-products')}
              >
                <i className="bi bi-circle" />
                <span>Add Products</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-products'); }}
                className={isSubActive('manage-products')}
              >
                <i className="bi bi-circle" />
                <span>Manage Products</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('print-barcodes'); }}
                className={isSubActive('print-barcodes')}
              >
                <i className="bi bi-circle" />
                <span>Print Bar Codes</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('import-from-excel'); }}
                className={isSubActive('import-from-excel')}
              >
                <i className="bi bi-circle" />
                <span>Import From Excel</span>
              </a>
            </li>
          </ul>
        </li>{/* End Products Nav */}

        {/* Sales */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive(['sales', 'set-discounts', 'manage-discounts', 'exchange-rate', 'admin-layby'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#sales-nav"
            data-bs-toggle={firstLogin ? '' : 'collapse'}
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-cash-stack" />
            <span>Sales</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="sales-nav" className={`nav-content ${isExpanded(['sales', 'set-discounts', 'manage-discounts', 'exchange-rate', 'admin-layby'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('sales'); }}
                className={isSubActive('sales')}
              >
                <i className="bi bi-circle" />
                <span>Sales</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('set-discounts'); }}
                className={isSubActive('set-discounts')}
              >
                <i className="bi bi-circle" />
                <span>Set Discounts</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-discounts'); }}
                className={isSubActive('manage-discounts')}
              >
                <i className="bi bi-circle" />
                <span>Manage Discounts</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('exchange-rate'); }}
                className={isSubActive('exchange-rate')}
              >
                <i className="bi bi-circle" />
                <span>Exchange Rate</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('admin-layby'); }}
                className={isSubActive('admin-layby')}
              >
                <i className="bi bi-circle" />
                <span>Layby</span>
              </a>
            </li>
          </ul>
        </li>{/* End Sales Nav */}

        {/* Stocks */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive(['new-stock-entry', 'manage-stocks', 'stock-movement', 'return-to-warehouse', 'transfer-stocks', 'low-stock', 'stock-adjustments'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#stocks-nav"
            data-bs-toggle={firstLogin ? '' : 'collapse'}
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-boxes" />
            <span>Stocks</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="stocks-nav" className={`nav-content ${isExpanded(['new-stock-entry', 'manage-stocks', 'stock-movement', 'return-to-warehouse', 'transfer-stocks', 'low-stock', 'stock-adjustments'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('new-stock-entry'); }}
                className={isSubActive('new-stock-entry')}
              >
                <i className="bi bi-circle" />
                <span>New Stock Entry</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-stocks'); }}
                className={isSubActive('manage-stocks')}
              >
                <i className="bi bi-circle" />
                <span>Manage Stocks</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('stock-adjustments'); }}
                className={isSubActive('stock-adjustments')}
              >
                <i className="bi bi-circle" />
                <span>Stock Adjustments</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('stock-movement'); }}
                className={isSubActive('stock-movement')}
              >
                <i className="bi bi-circle" />
                <span>Stock Movement</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('return-to-warehouse'); }}
                className={isSubActive('return-to-warehouse')}
              >
                <i className="bi bi-circle" />
                <span>Return to Warehouse</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('low-stock'); }}
                className={isSubActive('low-stock')}
              >
                <i className="bi bi-circle" />
                <span>Low Stock</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('transfer-stocks'); }}
                className={isSubActive('transfer-stocks')}
              >
                <i className="bi bi-circle" />
                <span>Transfer Stocks</span>
              </a>
            </li>
          </ul>
        </li>{/* End Stocks Nav */}

        {/* Categories */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive(['add-categories', 'manage-categories'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#categories-nav"
            data-bs-toggle={firstLogin ? '' : 'collapse'}
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-tags" />
            <span>Categories</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="categories-nav" className={`nav-content ${isExpanded(['add-categories', 'manage-categories'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('add-categories'); }}
                className={isSubActive('add-categories')}
              >
                <i className="bi bi-circle" />
                <span>Add Categories</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-categories'); }}
                className={isSubActive('manage-categories')}
              >
                <i className="bi bi-circle" />
                <span>Manage Categories</span>
              </a>
            </li>
          </ul>
        </li>{/* End Categories Nav */}

        {/* Branches */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive(['add-branch', 'manage-branches'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#branches-nav"
            data-bs-toggle={firstLogin ? '' : 'collapse'}
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-shop" />
            <span>Branches</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="branches-nav" className={`nav-content ${isExpanded(['add-branch', 'manage-branches'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('add-branch'); }}
                className={isSubActive('add-branch')}
              >
                <i className="bi bi-circle" />
                <span>Add Branch</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-branches'); }}
                className={isSubActive('manage-branches')}
              >
                <i className="bi bi-circle" />
                <span>Manage Branches</span>
              </a>
            </li>
          </ul>
        </li>{/* End Branches Nav */}

        {/* Warehouses */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive(['add-warehouse', 'manage-warehouses'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#warehouses-nav"
            data-bs-toggle={firstLogin ? '' : 'collapse'}
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-building" />
            <span>Warehouses</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="warehouses-nav" className={`nav-content ${isExpanded(['add-warehouse', 'manage-warehouses'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('add-warehouse'); }}
                className={isSubActive('add-warehouse')}
              >
                <i className="bi bi-circle" />
                <span>Add Warehouse</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-warehouses'); }}
                className={isSubActive('manage-warehouses')}
              >
                <i className="bi bi-circle" />
                <span>Manage Warehouses</span>
              </a>
            </li>
          </ul>
        </li>{/* End Warehouses Nav */}

        {/* Expenses */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive(['add-expense', 'manage-expenses', 'create-expense-category', 'manage-expense-category'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#expenses-nav"
            data-bs-toggle={firstLogin ? '' : 'collapse'}
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-cash-coin" />
            <span>Expenses</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="expenses-nav" className={`nav-content ${isExpanded(['add-expense', 'manage-expenses', 'create-expense-category', 'manage-expense-category'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('add-expense'); }}
                className={isSubActive('add-expense')}
              >
                <i className="bi bi-circle" />
                <span>Add Expense</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-expenses'); }}
                className={isSubActive('manage-expenses')}
              >
                <i className="bi bi-circle" />
                <span>Manage Expense</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('create-expense-category'); }}
                className={isSubActive('create-expense-category')}
              >
                <i className="bi bi-circle" />
                <span>Create Expense Category</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-expense-category'); }}
                className={isSubActive('manage-expense-category')}
              >
                <i className="bi bi-circle" />
                <span>Manage Expense Category</span>
              </a>
            </li>
          </ul>
        </li>{/* End Expenses Nav */}

        {/* Suppliers */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive(['add-supplier', 'manage-suppliers'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#suppliers-nav"
            data-bs-toggle={firstLogin ? '' : 'collapse'}
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-truck" />
            <span>Suppliers</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="suppliers-nav" className={`nav-content ${isExpanded(['add-supplier', 'manage-suppliers'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('add-supplier'); }}
                className={isSubActive('add-supplier')}
              >
                <i className="bi bi-circle" />
                <span>Add Supplier</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('manage-suppliers'); }}
                className={isSubActive('manage-suppliers')}
              >
                <i className="bi bi-circle" />
                <span>Manage Suppliers</span>
              </a>
            </li>
          </ul>
        </li>{/* End Suppliers Nav */}

        {/* Reports */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive(['reports', 'profitnloss'])} ${firstLogin ? 'disabled' : ''}`}
            data-bs-target="#reports-nav"
            data-bs-toggle={firstLogin ? '' : 'collapse'}
            href="#"
            onClick={(e) => { e.preventDefault(); }}
            style={firstLogin ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            <i className="bi bi-file-earmark-text" />
            <span>Reports</span>
            <i className="bi bi-chevron-down ms-auto" />
          </a>
          <ul id="reports-nav" className={`nav-content ${isExpanded(['reports', 'profitnloss'])}`} data-bs-parent="#sidebar-nav">
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('reports'); }}
                className={isSubActive('reports')}
              >
                <i className="bi bi-circle" />
                <span>Analytics Report</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                onClick={(e) => { e.preventDefault(); handleMenuClick('profitnloss'); }}
                className={isSubActive('profitnloss')}
              >
                <i className="bi bi-circle" />
                <span>Profit &amp; Loss</span>
              </a>
            </li>
          </ul>
        </li>{/* End Reports Nav */}

        {/* Profile */}
        <li className="nav-item">
          <a
            className={`nav-link ${isActive('profile')}`}
            href="#"
            onClick={(e) => { e.preventDefault(); handleMenuClick('profile'); }}
          >
            <i className="bi bi-person" />
            <span>Profile</span>
          </a>
        </li>{/* End Profile Nav */}
      </ul>
    </aside>

  )
}

export default SideBar